import React, { useId } from "react";
import styled from "styled-components";
import { bodyType, colorTransition, unselectable } from "./base/mixins";

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
	label?: React.ReactNode;
}

/**
 * §8: 20×20, 3px radius, 1px border, white fill. Checked is the purple fill —
 * not the primary lime — because the check glyph is white and white on lime is
 * 2.70:1. §1 covers the split.
 *
 * Checkbox rows sit 16px apart (§7); that spacing belongs to the container.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
	({ label, className, id, ...rest }, ref) => {
		const generated = useId();
		const inputId = id ?? generated;

		return (
			<Row className={className} htmlFor={inputId}>
				<input ref={ref} id={inputId} type="checkbox" {...rest} />
				<span className="hds-checkbox__box" aria-hidden>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
						<path d="M5 12.5l5 5 9-10" />
					</svg>
				</span>
				{label ? <span className="hds-checkbox__label">{label}</span> : null}
			</Row>
		);
	},
);

Checkbox.displayName = "Checkbox";

const Row = styled.label`
	${unselectable}
	/* The containing block of the hidden input below. Without it the input
	   positions against the nearest positioned ancestor — in a modal that is the
	   fixed scrim — so a checkbox further down a scrolling list escapes that
	   list's overflow, makes the scrim scrollable, and focusing it scrolls the
	   whole dialog out of view. */
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: var(--space-2);
	cursor: pointer;

	input {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0;
		width: var(--size-checkbox);
		height: var(--size-checkbox);
		margin: 0;
		cursor: pointer;
	}

	.hds-checkbox__box {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: none;
		width: var(--size-checkbox);
		height: var(--size-checkbox);
		border: var(--border-default);
		border-radius: var(--radius-sm);
		background: var(--color-surface);
		color: transparent;
		${colorTransition}

		/* The glyph is inset within the 20px box rather than filling it. */
		svg {
			width: calc(var(--size-checkbox) * 0.7);
			height: calc(var(--size-checkbox) * 0.7);
		}
	}

	.hds-checkbox__label {
		${bodyType}
	}

	&:hover input:not(:disabled) + .hds-checkbox__box {
		border-color: var(--color-border-strong);
	}

	input:checked + .hds-checkbox__box {
		background: var(--color-secondary-fill);
		border-color: var(--color-secondary-fill);
		color: var(--color-text-on-secondary);
	}

	input:focus-visible + .hds-checkbox__box {
		outline: var(--focus-ring);
		outline-offset: var(--focus-ring-offset);
	}

	input:disabled ~ * {
		opacity: 0.45;
	}
	input:disabled {
		cursor: not-allowed;
	}
`;

export default Checkbox;
